import { Eye, EyeOff, Unlink, MapPin, Clock, Radio } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatDistanceStrict } from "date-fns";
import type { CoverageSession } from "@/hooks/useNetworkCoverage";

interface CoverageSessionCardProps {
  session: CoverageSession;
  visible: boolean;
  onToggleVisibility: () => void;
  onUnlink: () => void;
}

export function CoverageSessionCard({
  session,
  visible,
  onToggleVisibility,
  onUnlink,
}: CoverageSessionCardProps) {
  const startDate = new Date(session.started_at);
  const endDate = session.ended_at ? new Date(session.ended_at) : null;

  const duration = endDate
    ? formatDistanceStrict(endDate, startDate)
    : "In progress";

  const formatDateTime = (date: Date) => {
    return date.toLocaleString("de-CH", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatBitrate = (kbps: number) => {
    if (kbps >= 1000) {
      return `${(kbps / 1000).toFixed(1)} Mbps`;
    }
    return `${kbps} kbps`;
  };

  return (
    <Card className={visible ? "ring-2 ring-primary" : ""}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Radio className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="font-medium truncate">
                {session.device_name || session.device_uid || "Unknown device"}
              </span>
              {session.has_gpx && (
                <Badge variant="secondary" className="flex-shrink-0">
                  <MapPin className="h-3 w-3 mr-1" />
                  GPX
                </Badge>
              )}
            </div>

            <div className="space-y-1 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Clock className="h-3 w-3" />
                <span>{formatDateTime(startDate)}</span>
                {endDate && (
                  <>
                    <span>→</span>
                    <span>{formatDateTime(endDate)}</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-4">
                <span>Duration: {duration}</span>
                {session.summary_stats?.avg_bitrate_kbps && (
                  <span>
                    Avg: {formatBitrate(session.summary_stats.avg_bitrate_kbps)}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant={visible ? "default" : "outline"}
              size="icon"
              onClick={onToggleVisibility}
              title={visible ? "Hide on map" : "Show on map"}
            >
              {visible ? (
                <Eye className="h-4 w-4" />
              ) : (
                <EyeOff className="h-4 w-4" />
              )}
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={onUnlink}
              title="Unlink from project"
            >
              <Unlink className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
